import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Upload,
  App as AntApp,
} from "antd";
import {
  EditOutlined,
  InboxOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  SearchOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import type { RcFile } from "antd/es/upload";

import { catalogApi } from "../api/catalog";
import { useTableScrollY } from "../utils/useTableScrollY";
import { enterprisesApi } from "../api/enterprises";
import type { Category, Enterprise, Product } from "../api/types";
import { PageHeader } from "../components/PageHeader";
import { Pill } from "../components/Pill";
import { formatINR } from "../utils/format";

const HSN_REGEX = /^[0-9]{4,8}$/;

export function Catalog() {
  const { notification } = AntApp.useApp();
  const [cats, setCats] = useState<Category[]>([]);
  const [activeCat, setActiveCat] = useState<string | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [enterprises, setEnterprises] = useState<Enterprise[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [seriesFilter, setSeriesFilter] = useState<string | undefined>();
  const [showInactive, setShowInactive] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const tableWrapRef = useRef<HTMLDivElement>(null);
  const [specsProduct, setSpecsProduct] = useState<Product | null>(null);

  const [addCatOpen, setAddCatOpen] = useState(false);
  const [addProdOpen, setAddProdOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editProduct, setEditProduct] = useState<Product | null>(null);
  const [colorStockProduct, setColorStockProduct] = useState<Product | null>(null);
  const [colorStockRows, setColorStockRows] = useState<{ colour: string; quantity: number; key: string }[]>([]);
  const [colorStockSaving, setColorStockSaving] = useState(false);

  const [catForm] = Form.useForm();
  const [prodForm] = Form.useForm();
  const [editForm] = Form.useForm();

  useEffect(() => {
    if (editProduct) {
      editForm.setFieldsValue({
        sku: editProduct.sku || "",
        name: editProduct.name,
        model: editProduct.model,
        series: editProduct.series,
        sweep_mm: editProduct.sweep_mm,
        colour: editProduct.colour,
        base_price: editProduct.base_price ? Number(editProduct.base_price) : null,
        mrp: editProduct.mrp ? Number(editProduct.mrp) : null,
        quantity: editProduct.quantity ?? 0,
        hsn_code: editProduct.hsn_code || "",
        gst_percentage:
          editProduct.gst_percentage !== null && editProduct.gst_percentage !== undefined
            ? Number(editProduct.gst_percentage)
            : null,
        enterprise: editProduct.enterprise || undefined,
        is_active: editProduct.is_active,
      });
    }
  }, [editProduct, editForm]);

  useEffect(() => {
    enterprisesApi
      .list({ is_active: true, page_size: 200 })
      .then((res) => setEnterprises(res.results))
      .catch(() => {
        // Non-superusers may have read-only access; treat 403 as empty list.
        setEnterprises([]);
      });
  }, []);

  const loadCats = useCallback(async () => {
    const res = await catalogApi.listCategories();
    setCats(res.results);
    if (!activeCat && res.results.length > 0) {
      setActiveCat(res.results[0].id);
    }
  }, [activeCat]);

  const loadProducts = useCallback(async () => {
    if (!activeCat) return;
    setLoading(true);
    try {
      const params: Record<string, unknown> = { category: activeCat, page, page_size: pageSize };
      if (search) params.search = search;
      if (seriesFilter) params.series = seriesFilter;
      if (!showInactive) params.is_active = true;
      const res = await catalogApi.listProducts(params);
      setProducts(res.results);
      setTotal(res.count);
    } finally {
      setLoading(false);
    }
  }, [activeCat, page, pageSize, search, seriesFilter, showInactive]);

  useEffect(() => {
    loadCats();
  }, [loadCats]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const seriesOptions = useMemo(() => {
    const seen = new Set<string>();
    products.forEach((p) => p.series && seen.add(p.series));
    return Array.from(seen).sort().map((s) => ({ label: s, value: s }));
  }, [products]);

  const addCat = async () => {
    const vals = await catForm.validateFields();
    await catalogApi.createCategory(vals);
    catForm.resetFields();
    setAddCatOpen(false);
    loadCats();
  };

  const addProd = async () => {
    const vals = await prodForm.validateFields();
    await catalogApi.createProduct({ ...vals, category: activeCat });
    prodForm.resetFields();
    setAddProdOpen(false);
    loadProducts();
  };

  const saveEdit = async () => {
    if (!editProduct) return;
    try {
      const vals = await editForm.validateFields();
      await catalogApi.updateProduct(editProduct.id, vals);
      notification.success({ message: "Product updated" });
      setEditProduct(null);
      loadProducts();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      if (err.response) {
        notification.error({ message: err.response.data?.detail || "Update failed" });
      }
    }
  };

  const openColorStockModal = (product: Product) => {
    const existing = (product.color_stocks || []).map((cs) => ({
      colour: cs.colour,
      quantity: cs.quantity,
      key: cs.colour,
    }));
    setColorStockRows(
      existing.length > 0 ? existing : [{ colour: "", quantity: 0, key: "new-0" }]
    );
    setColorStockProduct(product);
  };

  const saveColorStocks = async () => {
    if (!colorStockProduct) return;
    setColorStockSaving(true);
    try {
      const validRows = colorStockRows.filter((r) => r.colour.trim() !== "");
      await catalogApi.updateProduct(colorStockProduct.id, {
        color_stocks: validRows.map((r) => ({ colour: r.colour.trim(), quantity: r.quantity })),
      } as Partial<Product>);
      notification.success({ message: "Stock updated" });
      setColorStockProduct(null);
      loadProducts();
    } catch {
      notification.error({ message: "Could not update stock" });
    } finally {
      setColorStockSaving(false);
    }
  };

  const toggleActive = async (row: Product) => {
    try {
      await catalogApi.updateProduct(row.id, { is_active: !row.is_active });
      loadProducts();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      notification.error({ message: err.response?.data?.detail || "Status change failed" });
    }
  };

  const bulkUpload = async (file: RcFile) => {
    if (!activeCat) {
      notification.error({ message: "Select a category before uploading" });
      return false;
    }
    try {
      const res = await catalogApi.bulkUploadProducts(file, activeCat);
      const errorTail =
        res.failed_rows > 0 ? `, ${res.failed_rows} errors` : "";
      notification.success({
        message: `Imported into ${res.category}`,
        description: `${res.created_rows} created, ${res.updated_rows} updated${errorTail}`,
        duration: 6,
      });
      if (res.errors?.length) {
        // Surface up to 3 row-level errors so the admin sees what failed.
        notification.warning({
          message: "Some rows were skipped",
          description: res.errors
            .slice(0, 3)
            .map((e) => `row ${e.row_number}: ${e.reason}`)
            .join("\n"),
          duration: 10,
        });
      }
      setBulkOpen(false);
      loadProducts();
      loadCats();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      notification.error({ message: err.response?.data?.detail || "Upload failed" });
    }
    return false;
  };

  // Re-measure when the pager appears/disappears or changes height.
  const scrollY = useTableScrollY(tableWrapRef, [loading, total, pageSize]);

  const activeCatObj = cats.find((c) => c.id === activeCat);

  return (
    // Locked to the viewport (56px = the sticky topnav) so the window itself
    // never scrolls; the category list and the table body scroll on their own.
    <div
      style={{
        display: "flex",
        maxWidth: 1840,
        margin: "0 auto",
        height: "calc(100vh - 56px)",
        overflow: "hidden",
      }}
    >
      <aside
        style={{
          width: 240,
          flexShrink: 0,
          padding: "32px 16px 32px 32px",
          borderRight: "1px solid var(--border)",
          overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 500 }}>Categories</div>
          <Button type="text" icon={<PlusOutlined />} onClick={() => setAddCatOpen(true)} />
        </div>
        {cats.map((c) => {
          const isActive = c.id === activeCat;
          return (
            <div
              key={c.id}
              onClick={() => {
                setActiveCat(c.id);
                setPage(1);
                setSeriesFilter(undefined);
              }}
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "8px 10px",
                borderRadius: 6,
                fontSize: 13,
                cursor: "pointer",
                marginBottom: 2,
                background: isActive ? "var(--brand-soft)" : "transparent",
                color: isActive ? "var(--brand-hover)" : "var(--text-primary)",
                fontWeight: isActive ? 500 : 400,
              }}
            >
              <span>{c.name}</span>
              <span className="small-muted">({c.product_count})</span>
            </div>
          );
        })}
      </aside>

      <main
        style={{
          flex: 1,
          minWidth: 0,
          padding: 32,
          display: "flex",
          flexDirection: "column",
          // `auto`, not `hidden`: on a very short window the table clamps to its
          // 160px floor, and this lets the pager scroll into reach instead of
          // being clipped off the bottom.
          overflow: "auto",
        }}
      >
        <PageHeader
          title={activeCatObj?.name || "Inventory"}
          subtitle={`${total} products`}
          right={
            <Space>
              <Button
                icon={<UploadOutlined />}
                disabled={!activeCat}
                onClick={() => setBulkOpen(true)}
              >
                Import Price List
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                disabled={!activeCat}
                onClick={() => setAddProdOpen(true)}
              >
                Add Product
              </Button>
            </Space>
          }
        />

        <div className="toolbar-row">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search name, model, colour, series…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            style={{ maxWidth: 320 }}
          />
          <Select
            allowClear
            placeholder="All series"
            value={seriesFilter}
            options={seriesOptions}
            onChange={(v) => {
              setSeriesFilter(v);
              setPage(1);
            }}
            style={{ minWidth: 180 }}
          />
          <Checkbox
            checked={showInactive}
            onChange={(e) => {
              setShowInactive(e.target.checked);
              setPage(1);
            }}
          >
            Show inactive
          </Checkbox>
        </div>

        <div ref={tableWrapRef} style={{ flex: 1, minHeight: 0 }}>
          <Table
            rowKey="id"
            loading={loading}
            dataSource={products}
            scroll={{ y: scrollY }}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              onChange: (p, ps) => {
                setPage(p);
                setPageSize(ps);
              },
            }}
            columns={[
              {
                title: "Name",
                width: 260,
                dataIndex: "name",
                render: (v: string, row: Product) => (
                  <div>
                    <div style={{ fontWeight: 500 }}>{v}</div>
                    {row.model && <div className="small-muted mono">{row.model}</div>}
                    {row.sku && <div className="small-muted mono" style={{ fontSize: 11 }}>SKU: {row.sku}</div>}
                  </div>
                ),
              },
              {
                title: "Series",
                width: 110,
                dataIndex: "series",
                render: (v: string) => v ? <Tag>{v}</Tag> : <span className="small-muted">—</span>,
              },
              {
                title: "Sweep",
                width: 100,
                dataIndex: "sweep_mm",
                align: "right" as const,
                render: (v: number | null) => v ? <span className="tabular">{v} mm</span> : <span className="small-muted">—</span>,
              },
              { title: "Colour", dataIndex: "colour", width: 110, render: (v: string) => v || <span className="small-muted">—</span> },
              {
                title: "MRP",
                width: 110,
                dataIndex: "mrp",
                align: "right" as const,
                render: (v: string | null) =>
                  v ? (
                    <span className="tabular" style={{ color: "var(--text-secondary)" }}>{formatINR(v)}</span>
                  ) : (
                    <span className="small-muted">—</span>
                  ),
              },
              {
                title: "Dealer Price",
                width: 125,
                dataIndex: "base_price",
                align: "right" as const,
                render: (v: string | null) =>
                  v ? (
                    <span className="tabular">{formatINR(v)}</span>
                  ) : (
                    <Tag color="warning">Not set</Tag>
                  ),
              },
              {
                title: "Stock",
                width: 130,
                dataIndex: "quantity",
                align: "right" as const,
                render: (v: number, row: Product) => (
                  <div style={{ textAlign: "right" }}>
                    {v > 0 ? (
                      <span className="tabular">{v}</span>
                    ) : (
                      <Tag color="error">Out</Tag>
                    )}
                    {row.color_stocks && row.color_stocks.length > 0 && (
                      <div style={{ fontSize: 11, color: "var(--text-secondary)" }}>
                        {row.color_stocks.length} colour{row.color_stocks.length !== 1 ? "s" : ""}
                      </div>
                    )}
                    <div>
                      <Button
                        type="link"
                        size="small"
                        style={{ padding: 0, fontSize: 11, height: "auto" }}
                        onClick={() => openColorStockModal(row)}
                      >
                        Edit by colour
                      </Button>
                    </div>
                  </div>
                ),
              },
              {
                title: "HSN",
                width: 105,
                dataIndex: "hsn_code",
                render: (v: string) =>
                  v ? <span className="mono">{v}</span> : <span className="small-muted">—</span>,
              },
              {
                title: "GST %",
                width: 85,
                dataIndex: "gst_percentage",
                align: "right" as const,
                render: (v: string | null) =>
                  v !== null && v !== undefined && v !== "" ? (
                    <span className="tabular">{Number(v)}%</span>
                  ) : (
                    <span className="small-muted">—</span>
                  ),
              },
              {
                title: "Enterprise",
                width: 130,
                dataIndex: "enterprise_name",
                render: (v: string | null) =>
                  v ? <Tag>{v}</Tag> : <span className="small-muted">—</span>,
              },
              {
                title: "Status",
                width: 120,
                dataIndex: "is_active",
                render: (v: boolean, row: Product) => (
                  <Popconfirm
                    title={v ? "Deactivate this product?" : "Reactivate this product?"}
                    description={
                      v
                        ? "Employees won't see it in the order app."
                        : "Employees will be able to order it again."
                    }
                    okText={v ? "Deactivate" : "Reactivate"}
                    cancelText="Cancel"
                    onConfirm={() => toggleActive(row)}
                  >
                    <Space size={6} style={{ cursor: "pointer" }}>
                      <Switch size="small" checked={v} />
                      <Pill kind={v ? "active" : "inactive"} />
                    </Space>
                  </Popconfirm>
                ),
              },
              {
                title: "",
                key: "_actions",
                width: 160,
                render: (_: unknown, row: Product) => (
                  <Space size={4}>
                    <Button
                      type="text"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => setEditProduct(row)}
                    >
                      Edit
                    </Button>
                    <Button
                      type="text"
                      size="small"
                      icon={<InfoCircleOutlined />}
                      onClick={() => setSpecsProduct(row)}
                      disabled={!row.specs || Object.keys(row.specs).length === 0}
                    >
                      Specs
                    </Button>
                  </Space>
                ),
              },
            ]}
          />
        </div>
      </main>

      <Modal title="Add Category" open={addCatOpen} onCancel={() => setAddCatOpen(false)} onOk={addCat} okText="Create">
        <Form layout="vertical" form={catForm} requiredMark={false}>
          <Form.Item label="Name" name="name" rules={[{ required: true, max: 100 }]}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Add Product" open={addProdOpen} onCancel={() => setAddProdOpen(false)} onOk={addProd} okText="Create">
        <Form layout="vertical" form={prodForm} requiredMark={false}>
          <Form.Item label="SKU" name="sku" extra="Optional. Used as the unique key during bulk imports.">
            <Input placeholder="e.g. ACGC-AVNDAC 65" />
          </Form.Item>
          <Form.Item label="Name" name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Model" name="model">
            <Input placeholder="e.g. 1200mm" />
          </Form.Item>
          <Form.Item label="Series" name="series">
            <Input placeholder="e.g. SilentPro, Energion" />
          </Form.Item>
          <Form.Item label="Sweep (mm)" name="sweep_mm">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Colour" name="colour">
            <Input />
          </Form.Item>
          <Form.Item
            label="MRP (₹)"
            name="mrp"
            extra="Optional. Printed retail price."
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            label="Dealer Price (₹)"
            name="base_price"
            extra="Optional. If blank, employees will enter the price when ordering."
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            label="Quantity (stock)"
            name="quantity"
            initialValue={0}
            extra="Stock decreases automatically when an order is accepted."
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            label="HSN Code"
            name="hsn_code"
            extra="Optional. 4–8 digit HSN/SAC code."
            rules={[
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  return HSN_REGEX.test(value)
                    ? Promise.resolve()
                    : Promise.reject(new Error("HSN code must be 4 to 8 digits"));
                },
              },
            ]}
          >
            <Input maxLength={8} placeholder="e.g. 84145100" />
          </Form.Item>
          <Form.Item
            label="GST Percentage (%)"
            name="gst_percentage"
            extra="Optional. 0 to 100."
          >
            <InputNumber
              min={0}
              max={100}
              step={0.01}
              style={{ width: "100%" }}
              placeholder="e.g. 18"
            />
          </Form.Item>
          <Form.Item
            label="Enterprise"
            name="enterprise"
            extra="Company this product belongs to. Configured by the Superuser."
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={enterprises.length === 0 ? "No enterprises configured" : "Select enterprise"}
              options={enterprises.map((e) => ({ label: e.name, value: e.id }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editProduct ? `Edit ${editProduct.name}` : "Edit Product"}
        open={!!editProduct}
        onCancel={() => setEditProduct(null)}
        onOk={saveEdit}
        okText="Save"
        destroyOnClose
      >
        <Form layout="vertical" form={editForm} requiredMark={false} preserve={false}>
          <Form.Item label="SKU" name="sku">
            <Input placeholder="e.g. ACGC-AVNDAC 65" />
          </Form.Item>
          <Form.Item label="Name" name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Model" name="model">
            <Input placeholder="e.g. 1200mm" />
          </Form.Item>
          <Form.Item label="Series" name="series">
            <Input placeholder="e.g. SilentPro, Energion" />
          </Form.Item>
          <Form.Item label="Sweep (mm)" name="sweep_mm">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Colour" name="colour">
            <Input />
          </Form.Item>
          <Form.Item label="MRP (₹)" name="mrp" extra="Optional. Printed retail price.">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            label="Dealer Price (₹)"
            name="base_price"
            extra="Leave blank if employees should enter the price when ordering."
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            label="Quantity (stock)"
            name="quantity"
            extra="Stock decreases automatically when an order is accepted."
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            label="HSN Code"
            name="hsn_code"
            extra="Optional. 4–8 digit HSN/SAC code."
            rules={[
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  return HSN_REGEX.test(value)
                    ? Promise.resolve()
                    : Promise.reject(new Error("HSN code must be 4 to 8 digits"));
                },
              },
            ]}
          >
            <Input maxLength={8} placeholder="e.g. 84145100" />
          </Form.Item>
          <Form.Item
            label="GST Percentage (%)"
            name="gst_percentage"
            extra="Optional. 0 to 100."
          >
            <InputNumber min={0} max={100} step={0.01} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Enterprise" name="enterprise">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={enterprises.length === 0 ? "No enterprises configured" : "Select enterprise"}
              options={enterprises.map((e) => ({ label: e.name, value: e.id }))}
            />
          </Form.Item>
          <Form.Item label="Active" name="is_active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Import Price List → ${activeCatObj?.name || ""}`}
        open={bulkOpen}
        onCancel={() => setBulkOpen(false)}
        footer={null}
      >
        <div style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 10 }}>
          Accepts <code>.csv</code>, <code>.xlsx</code> and <code>.pdf</code> price lists.
          Column headings are auto-detected (SKU, MRP, retailer/dealer price, HSN, GST, etc.).
          Anything we don't recognise is preserved per-product under <strong>Specs</strong>.
          Re-uploading the same file updates existing products by SKU.
        </div>
        <Upload.Dragger
          name="file"
          accept=".csv,.xlsx,.xlsm,.pdf"
          multiple={false}
          beforeUpload={bulkUpload}
          showUploadList={false}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Drop file here or click to browse</p>
          <p className="ant-upload-hint" style={{ fontSize: 12 }}>
            CSV · Excel (xlsx) · PDF (text-based, not scanned images)
          </p>
        </Upload.Dragger>
      </Modal>

      <Drawer
        title={specsProduct ? `${specsProduct.name} — specs` : "Specs"}
        open={!!specsProduct}
        onClose={() => setSpecsProduct(null)}
        width={520}
      >
        {specsProduct && <SpecsView specs={specsProduct.specs} />}
      </Drawer>

      <Modal
        title={colorStockProduct ? `Stock by Colour — ${colorStockProduct.name}` : "Stock by Colour"}
        open={!!colorStockProduct}
        onCancel={() => setColorStockProduct(null)}
        onOk={saveColorStocks}
        confirmLoading={colorStockSaving}
        okText="Save"
        width={520}
        destroyOnClose
      >
        <div style={{ marginBottom: 12, fontSize: 13, color: "var(--text-secondary)" }}>
          Enter stock quantity per colour variant. Leave colour blank to skip that row.
        </div>
        {colorStockRows.map((row, i) => (
          <div
            key={row.key}
            style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}
          >
            <Input
              placeholder="Colour (e.g. White, Brown)"
              value={row.colour}
              style={{ flex: 1 }}
              onChange={(e) => {
                const updated = [...colorStockRows];
                updated[i] = { ...updated[i], colour: e.target.value };
                setColorStockRows(updated);
              }}
            />
            <InputNumber
              min={0}
              value={row.quantity}
              style={{ width: 110 }}
              placeholder="Qty"
              onChange={(v) => {
                const updated = [...colorStockRows];
                updated[i] = { ...updated[i], quantity: v ?? 0 };
                setColorStockRows(updated);
              }}
            />
            <Button
              type="text"
              danger
              size="small"
              onClick={() => setColorStockRows(colorStockRows.filter((_, idx) => idx !== i))}
            >
              ✕
            </Button>
          </div>
        ))}
        <Button
          type="dashed"
          style={{ width: "100%", marginTop: 4 }}
          onClick={() =>
            setColorStockRows([
              ...colorStockRows,
              { colour: "", quantity: 0, key: `new-${colorStockRows.length}` },
            ])
          }
        >
          + Add Colour
        </Button>
      </Modal>
    </div>
  );
}

function SpecsView({ specs }: { specs: Record<string, unknown> }) {
  const entries = Object.entries(specs);
  if (entries.length === 0) {
    return <div className="small-muted">No specs recorded.</div>;
  }
  return (
    <Descriptions column={1} size="small" bordered>
      {entries.map(([k, v]) => (
        <Descriptions.Item key={k} label={k}>
          {renderSpecValue(v)}
        </Descriptions.Item>
      ))}
    </Descriptions>
  );
}

function renderSpecValue(v: unknown): React.ReactNode {
  if (v === null || v === undefined) return <span className="small-muted">—</span>;
  if (Array.isArray(v)) {
    if (v.length === 0) return <span className="small-muted">—</span>;
    if (typeof v[0] === "string") {
      return <Space size={[4, 4]} wrap>{v.map((x, i) => <Tag key={i}>{String(x)}</Tag>)}</Space>;
    }
    return (
      <pre style={{ margin: 0, fontSize: 12, whiteSpace: "pre-wrap" }}>
        {JSON.stringify(v, null, 2)}
      </pre>
    );
  }
  if (typeof v === "object") {
    return (
      <pre style={{ margin: 0, fontSize: 12, whiteSpace: "pre-wrap" }}>
        {JSON.stringify(v, null, 2)}
      </pre>
    );
  }
  return String(v);
}
